import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || '/api'
const IDP_URL = import.meta.env.VITE_IDP_URL || 'https://idp.l6ecosystem.com'
const APP_CODE = import.meta.env.VITE_APP_CODE || 'contract'

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ── Request interceptor: attach JWT ─────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('jwt_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ── Response interceptor: handle 401 ────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('jwt_token')
      window.location.href = `${IDP_URL}/auth/login?app=${APP_CODE}`
    }
    return Promise.reject(error)
  }
)

// ── Auth ──────────────────────────────────────────────
export const authAPI = {
  me: () => api.get('/auth/me'),
  callback: (token: string) => api.post('/auth/callback', { token }),
  devLogin: (userId: number, companyId: number) =>
    api.post('/auth/dev-login', { user_id: userId, company_id: companyId }),
  devUsers: () => api.get('/auth/dev-users'),
}

// ── Companies ─────────────────────────────────────────
export const companiesAPI = {
  list: () => api.get('/companies'),
  current: () => api.get('/companies/current'),
  updateSettings: (data: any) => api.put('/companies/current/settings', data),
  uploadCertificate: (formData: FormData) =>
    api.post('/companies/current/settings/certificate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getById: (id: number) => api.get(`/companies/${id}`),
}

// ── Contract Types ────────────────────────────────────
export const contractTypesAPI = {
  list: () => api.get('/contract-types'),
  create: (data: any) => api.post('/contract-types', data),
  update: (id: number, data: any) => api.put(`/contract-types/${id}`, data),
  delete: (id: number) => api.delete(`/contract-types/${id}`),
}

// ── Templates ─────────────────────────────────────────
export const templatesAPI = {
  list: (params?: any) => api.get('/templates', { params }),
  getById: (id: number) => api.get(`/templates/${id}`),
  create: (data: any) => api.post('/templates', data),
  update: (id: number, data: any) => api.put(`/templates/${id}`, data),
  duplicate: (id: number) => api.post(`/templates/${id}/duplicate`),
  delete: (id: number) => api.delete(`/templates/${id}`),
  preview: (id: number) => api.get(`/templates/${id}/preview`),
}

// ── Contracts ─────────────────────────────────────────
export const contractsAPI = {
  list: (params?: any) => api.get('/contracts', { params }),
  getById: (id: number) => api.get(`/contracts/${id}`),
  create: (data: any) => api.post('/contracts', data),
  update: (id: number, data: any) => api.put(`/contracts/${id}`, data),
  submit: (id: number) => api.post(`/contracts/${id}/submit`),
  approve: (id: number) => api.post(`/contracts/${id}/approve`),
  reject: (id: number) => api.post(`/contracts/${id}/reject`),
  publish: (id: number) => api.post(`/contracts/${id}/publish`),
  archive: (id: number) => api.post(`/contracts/${id}/archive`),
  getRevisions: (id: number) => api.get(`/contracts/${id}/revisions`),
  getRevision: (id: number, rev: number) => api.get(`/contracts/${id}/revisions/${rev}`),
  getPdf: async (id: number) => {
    const resp = await api.get(`/contracts/${id}/pdf`, {
      responseType: 'blob',
    })
    return resp.data
  },
}

// ── Signatures ────────────────────────────────────────
export const signaturesAPI = {
  list: (contractId: number) => api.get(`/contracts/${contractId}/signatures`),
  send: (contractId: number, data: any) =>
    api.post(`/contracts/${contractId}/signatures/send`, data),
  sign: (contractId: number, data: any) =>
    api.post(`/contracts/${contractId}/signatures/sign`, data),
  verify: (contractId: number) =>
    api.post(`/contracts/${contractId}/signatures/verify`),
}

// ── IDP SSO ───────────────────────────────────────────
export const idpSSO = {
  getLoginUrl: () => `${IDP_URL}/auth/login?app=${APP_CODE}`,
  getRegisterUrl: () => `${IDP_URL}/auth/register?app=${APP_CODE}`,
  getAdminUrl: () => `${IDP_URL}/admin?app=${APP_CODE}`,
  launchUrl: () => `${IDP_URL}/api/access/${APP_CODE}`,
}

export default api
